import { useEffect, useState, useCallback } from 'react';
import { useSocketContext } from '../context/SocketContext';
import { toast } from 'react-toastify';

/**
 * Custom hook for queue-related socket events
 * @param {string} doctorId - The doctor ID to subscribe to
 * @param {object} options - Configuration options
 */
export const useQueueSocket = (doctorId, options = {}) => {
  const {
    isConnected,
    subscribeToQueue,
    unsubscribeFromQueue,
    addEventListener,
    removeEventListener,
  } = useSocketContext();

  const {
    showNotifications = true,
    onQueueUpdated = null,
    onPatientAdded = null,
    onPatientRemoved = null,
    onServiceStarted = null,
    onServiceCompleted = null,
    onQueueReordered = null,
    onAppointmentBooked = null,
  } = options;

  const [queueData, setQueueData] = useState(null);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [nextPatient, setNextPatient] = useState(null);
  const [summary, setSummary] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Handle queue updated event
  const handleQueueUpdated = useCallback((data) => {
    if (data.data) {
      setQueueData(data.data);
      setSummary(data.data.summary);
      setCurrentPatient(data.data.currentPatient);
      setNextPatient(data.data.nextPatient);
    }

    if (onQueueUpdated) {
      onQueueUpdated(data);
    }
  }, [onQueueUpdated]);

  // Handle patient added to queue
  const handlePatientAdded = useCallback((data) => {
    if (showNotifications) {
      toast.info(
        `${data.appointment?.patientName || 'Patient'} added to queue`,
        { position: 'top-right', autoClose: 3000 }
      );
    }

    if (onPatientAdded) {
      onPatientAdded(data);
    }
  }, [showNotifications, onPatientAdded]);

  // Handle patient removed from queue
  const handlePatientRemoved = useCallback((data) => {
    if (showNotifications) {
      toast.info(
        `${data.appointment?.patientName || 'Patient'} removed from queue`,
        { position: 'top-right', autoClose: 3000 }
      );
    }

    if (onPatientRemoved) {
      onPatientRemoved(data);
    }
  }, [showNotifications, onPatientRemoved]);

  // Handle service started
  const handleServiceStarted = useCallback((data) => {
    if (showNotifications) {
      toast.success(
        `Consultation started with ${data.appointment?.patientName || 'patient'}`,
        { position: 'top-right', autoClose: 3000 }
      );
    }

    if (onServiceStarted) {
      onServiceStarted(data);
    }
  }, [showNotifications, onServiceStarted]);

  // Handle service completed
  const handleServiceCompleted = useCallback((data) => {
    if (showNotifications) {
      toast.success(
        `Consultation completed for ${data.appointment?.patientName || 'patient'}`,
        { position: 'top-right', autoClose: 3000 }
      );
    }

    if (onServiceCompleted) {
      onServiceCompleted(data);
    }
  }, [showNotifications, onServiceCompleted]);

  // Handle queue reordered
  const handleQueueReordered = useCallback((data) => {
    if (showNotifications) {
      toast.info('Queue order has been updated', {
        position: 'top-right',
        autoClose: 3000,
      });
    }

    if (onQueueReordered) {
      onQueueReordered(data);
    }
  }, [showNotifications, onQueueReordered]);

  // Handle appointment booked (new scheduled appointment)
  const handleAppointmentBooked = useCallback((data) => {
    if (showNotifications) {
      toast.success(
        `New appointment: ${data.appointment?.patientName || 'Patient'} scheduled`,
        { position: 'top-right', autoClose: 4000 }
      );
    }

    if (onAppointmentBooked) {
      onAppointmentBooked(data);
    }
  }, [showNotifications, onAppointmentBooked]);

  // Handle subscription confirmation
  const handleSubscribed = useCallback((data) => {
    setIsSubscribed(true);
  }, []);

  // Handle unsubscription confirmation
  const handleUnsubscribed = useCallback((data) => {
    setIsSubscribed(false);
  }, []);

  // Subscribe to queue when connected and doctorId is available
  useEffect(() => {
    if (isConnected && doctorId) {
      subscribeToQueue(doctorId);

      // Setup all event listeners
      addEventListener('subscribed:queue', handleSubscribed);
      addEventListener('queue:updated', handleQueueUpdated);
      addEventListener('queue:patient-added', handlePatientAdded);
      addEventListener('queue:patient-removed', handlePatientRemoved);
      addEventListener('queue:service-started', handleServiceStarted);
      addEventListener('queue:service-completed', handleServiceCompleted);
      addEventListener('queue:reordered', handleQueueReordered);
      addEventListener('appointment:booked', handleAppointmentBooked);

      // Cleanup on unmount or when doctorId changes
      return () => {
        removeEventListener('subscribed:queue', handleSubscribed);
        removeEventListener('queue:updated', handleQueueUpdated);
        removeEventListener('queue:patient-added', handlePatientAdded);
        removeEventListener('queue:patient-removed', handlePatientRemoved);
        removeEventListener('queue:service-started', handleServiceStarted);
        removeEventListener('queue:service-completed', handleServiceCompleted);
        removeEventListener('queue:reordered', handleQueueReordered);
        removeEventListener('appointment:booked', handleAppointmentBooked);
        
        unsubscribeFromQueue(doctorId);
        setIsSubscribed(false);
      };
    }
  }, [
    isConnected,
    doctorId,
    subscribeToQueue,
    unsubscribeFromQueue,
    addEventListener,
    removeEventListener,
    handleSubscribed,
    handleQueueUpdated,
    handlePatientAdded,
    handlePatientRemoved,
    handleServiceStarted,
    handleServiceCompleted,
    handleQueueReordered,
    handleAppointmentBooked,
  ]);

  return {
    isConnected,
    isSubscribed,
    queueData,
    summary,
    currentPatient,
    nextPatient,
  };
};

export default useQueueSocket;
